import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { Stethoscope, Users, Briefcase, Calendar, Plane, ArrowRight, CheckCircle2 } from 'lucide-react';
export function Services() {
  const services = [{
    id: 'medical',
    title: 'Medical Appointments (NEMT)',
    icon: <Stethoscope className="w-8 h-8" data-id="element-1039" />,
    description: 'Non-emergency medical transportation to doctor visits, dialysis, physical therapy, and hospital discharges.',
    audience: 'Patients needing reliable transport to healthcare facilities.',
    vehicle: 'Wheelchair Vans & Ambulatory Sedans',
    assistance: 'Full Door-Through-Door Assistance',
    features: ['Wait-and-return options', 'Prescription pick-up on route', 'Coordination with facility staff'],
    bgImage: 'https://images.unsplash.com/photo-1587745416684-47953f16f02f?w=800&q=80'
  }, {
    id: 'senior',
    title: 'Senior Transportation',
    icon: <Users className="w-8 h-8" data-id="element-1040" />,
    description: 'Assisted rides for seniors to shopping centers, family visits, religious services, or social outings.',
    audience: 'Seniors who no longer drive but want to maintain independence.',
    vehicle: 'Comfortable Sedans & Accessible Vans',
    assistance: 'Arm-in-arm assistance available',
    features: ['Help with groceries/bags', 'Familiar, consistent drivers', 'Flexible scheduling'],
    bgImage: 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&q=80'
  }, {
    id: 'work',
    title: 'Work & Adult Day Programs',
    icon: <Briefcase className="w-8 h-8" data-id="element-1041" />,
    description: 'Regular scheduled transportation for individuals attending day programs, rehabilitation, or employment.',
    audience: 'Adults with disabilities attending daily programs.',
    vehicle: 'Multi-passenger Accessible Vans',
    assistance: 'Curb-to-Curb or Door-to-Door',
    features: ['Recurring schedule discounts', 'Direct billing to programs', 'Group ride coordination'],
    bgImage: 'https://images.unsplash.com/photo-1559416523-140ddc3d238c?w=800&q=80'
  }, {
    id: 'event',
    title: 'Event & Group Transportation',
    icon: <Calendar className="w-8 h-8" data-id="element-1042" />,
    description: 'Accessible transportation for group outings, weddings, family reunions, or special events.',
    audience: 'Families and groups needing inclusive transport options.',
    vehicle: 'Large Capacity Wheelchair Vans',
    assistance: 'Customized to group needs',
    features: ['Hourly booking rates', 'Multiple pickup locations', 'Weekend availability'],
    bgImage: 'https://images.unsplash.com/photo-1570733577524-3a047079e80d?w=800&q=80'
  }, {
    id: 'airport',
    title: 'Airport Transportation',
    icon: <Plane className="w-8 h-8" data-id="element-1043" />,
    description: 'Door-to-door service to and from regional airports with luggage and mobility device assistance.',
    audience: 'Travelers with mobility challenges.',
    vehicle: 'Spacious Vans for Luggage & Devices',
    assistance: 'Assistance to ticketing/baggage claim',
    features: ['Flight tracking', 'Extra luggage space', 'Early morning/late night service'],
    bgImage: 'https://images.unsplash.com/photo-1609521263047-f8f205293f24?w=800&q=80'
  }];
  return <div className="bg-warm-50 min-h-screen pb-20" data-id="element-1044">
      {/* Header */}
      <div className="bg-primary-900 text-white py-16" data-id="element-1045">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1046">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1047">Our Services</h1>
          <p className="text-xl text-primary-100 max-w-3xl mx-auto" data-id="element-1048">
            Comprehensive transportation solutions tailored to your specific
            mobility needs. We ensure you get to your destination safely,
            comfortably, and on time.
          </p>
        </div>
      </div>

      {/* Services List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8" data-id="element-1049">
        <div className="space-y-8" data-id="element-1050">
          {services.map((service, index) => <Card key={service.id} className="overflow-hidden shadow-lg border-none" data-id="element-1051">
              <div className="flex flex-col md:flex-row" data-id="element-1052">
                <div className="md:w-1/3 p-8 flex flex-col justify-center text-white relative overflow-hidden min-h-[280px]" data-id="element-1053">
                  <div className="absolute inset-0 bg-cover bg-center" style={{
                backgroundImage: `url(${service.bgImage})`
              }} data-id="element-1054" />
                  <div className="absolute inset-0 bg-black/65" data-id="element-1055" />
                  <div className="relative z-10" data-id="element-1056">
                    <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 backdrop-blur-sm" data-id="element-1057">
                      {service.icon}
                    </div>
                    <h2 className="text-2xl font-bold mb-4" data-id="element-1058">{service.title}</h2>
                    <p className="text-white/90 text-lg leading-relaxed" data-id="element-1059">
                      {service.description}
                    </p>
                  </div>
                </div>
                <div className="md:w-2/3 p-8 bg-white" data-id="element-1060">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8" data-id="element-1061">
                    <div data-id="element-1062">
                      <h3 className="text-sm font-bold text-warm-400 uppercase tracking-wider mb-2" data-id="element-1063">
                        Who It's For
                      </h3>
                      <p className="text-warm-800 font-medium" data-id="element-1064">
                        {service.audience}
                      </p>
                    </div>
                    <div data-id="element-1065">
                      <h3 className="text-sm font-bold text-warm-400 uppercase tracking-wider mb-2" data-id="element-1066">
                        Vehicles Used
                      </h3>
                      <p className="text-warm-800 font-medium" data-id="element-1067">
                        {service.vehicle}
                      </p>
                    </div>
                    <div data-id="element-1068">
                      <h3 className="text-sm font-bold text-warm-400 uppercase tracking-wider mb-2" data-id="element-1069">
                        Assistance Level
                      </h3>
                      <p className="text-warm-800 font-medium" data-id="element-1070">
                        {service.assistance}
                      </p>
                    </div>
                  </div>

                  <div className="mb-8" data-id="element-1071">
                    <h3 className="text-sm font-bold text-warm-400 uppercase tracking-wider mb-4" data-id="element-1072">
                      Key Features
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1073">
                      {service.features.map((feature, i) => <li key={i} className="flex items-start" data-id="element-1074">
                          <CheckCircle2 className="w-5 h-5 text-secondary-500 mr-2 flex-shrink-0" data-id="element-1075" />
                          <span className="text-warm-700" data-id="element-1076">{feature}</span>
                        </li>)}
                    </ul>
                  </div>

                  <Link to={`/booking?service=${service.id}`} data-id="element-1077">
                    <Button variant={index % 2 === 0 ? 'primary' : 'secondary'} data-id="element-1078">
                      Book This Service <ArrowRight className="ml-2 w-4 h-4" data-id="element-1079" />
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>)}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto mt-20 px-4 text-center" data-id="element-1080">
        <h2 className="text-2xl font-bold text-warm-900 mb-4" data-id="element-1081">
          Don't see exactly what you need?
        </h2>
        <p className="text-warm-600 mb-8" data-id="element-1082">
          We often accommodate special requests and unique transportation needs.
          Contact our team to discuss how we can help.
        </p>
        <Link to="/contact" data-id="element-1083">
          <Button variant="outline" size="lg" className="bg-white" data-id="element-1084">
            Contact Us to Discuss Custom Needs
          </Button>
        </Link>
      </div>
    </div>;
}